// ChallengeSizePage — Step 2: choose account size
// iOS HIG: inset-grouped list of $50K/$100K/$150K rows with price trailing

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChallengeLayout, IOSCheck, IOSList, IOSPrimaryButton, IOSRow, stepPath } from './ChallengeLayout';
import { SIZE_OPTIONS_BY_TIER } from './data';
import { loadDraft, setSize, type SizeKey, type TierName } from './store';
import { BrandLogo } from '../../../components/Brand';

export function ChallengeSizePage() {
  const nav = useNavigate();
  const draft = loadDraft();
  const tier: TierName | undefined = draft.tier;
  const [selected, setSelected] = useState<SizeKey | null>(draft.size || null);

  useEffect(() => {
    if (!tier) nav('/portal/challenge/tier', { replace: true });
  }, [tier, nav]);

  if (!tier) return null;

  const options = SIZE_OPTIONS_BY_TIER[tier];

  function onContinue() {
    if (!selected) return;
    setSize(selected);
    nav(stepPath('platform'));
  }

  const sel = options.find((o) => o.size === selected);

  return (
    <ChallengeLayout current="size" subtitle={tier ? `${tier} tier` : undefined}>
      <div>
        <div className="px-1 pb-2 text-[13px] font-normal uppercase tracking-wide text-[#8E8E93]">Account size</div>
        <IOSList>
          {options.map((o, i) => {
            const isSel = selected === o.size;
            return (
              <IOSRow
                key={o.size}
                testId={`size-card-${o.size}`}
                selected={isSel}
                onClick={() => setSelected(o.size)}
                showDivider={i < options.length - 1}
                leading={<BrandLogo name="FP" size={28} />}
                title={<span className="text-[17px]">${o.size},000 funded</span>}
                subtitle={`${o.profitShare}% profit split · Free activation`}
                trailing={
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-[17px] font-semibold tabular-nums text-[#1C1C1E]">${o.price}</div>
                    </div>
                    <IOSCheck selected={isSel} />
                  </div>
                }
              />
            );
          })}
        </IOSList>
      </div>

      {sel && (
        <div className="mt-8">
          <div className="px-1 pb-2 text-[13px] font-normal uppercase tracking-wide text-[#8E8E93]">Challenge rules</div>
          <div className="rounded-[12px] bg-white px-4 py-1">
            <RuleRow label="Profit target" value={`$${sel.profitTarget.toLocaleString()}`} />
            <RuleRow label="Max drawdown" value={`$${sel.maxDrawdown.toLocaleString()}`} />
            <RuleRow label="Daily loss" value={`$${sel.dailyDrawdown.toLocaleString()}`} />
            <RuleRow label="Reset fee" value={`$${sel.resetPrice}`} last />
          </div>
          <p className="mt-3 px-1 text-[13px] text-[#8E8E93]">
            EOD Balance drawdown. Hit your profit target without breaching your max drawdown to get funded.
          </p>
        </div>
      )}

      <div className="mt-8">
        <IOSPrimaryButton onClick={onContinue} disabled={!selected}>
          Continue
        </IOSPrimaryButton>
      </div>
    </ChallengeLayout>
  );
}

function RuleRow({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <div className={['flex items-center justify-between py-3', last ? '' : 'border-b border-[#C6C6C8]/55'].join(' ')}>
      <span className="text-[15px] text-[#1C1C1E]">{label}</span>
      <span className="text-[15px] font-semibold tabular-nums text-[#1C1C1E]">{value}</span>
    </div>
  );
}
